/**
 * The Agents endpoints, named once.
 *
 * Thin, like every other client here: the shared `api` unwraps the envelope and
 * throws a typed error, so these exist so no component builds a URL by hand.
 */

import { api } from '@/core/api/base.api.client'
import type {
  Agent,
  AgentAnswer,
  AgentDraft,
  AskBody,
  AgentMessage,
  AgentOptions,
  AgentThread,
  ConnectionModels,
} from './types'

const base = '/brain/assistants'

export const agentsApi = {
  list: () => api.get<{ assistants: Agent[] }>(base).then((d) => d.assistants),
  /** The models and graphs the create dialog needs — one request, not two. */
  options: () => api.get<AgentOptions>(`${base}/options`),
  /**
   * The models one connection's own key can call.
   *
   * Separate from `options` on purpose: this list belongs to someone else's
   * key, and fetching every connection's models to draw one dialog would make
   * opening it cost a round trip per connection.
   */
  connectionModels: (connectionId: string) =>
    api.get<ConnectionModels>(
      `${base}/options/models?connection_id=${encodeURIComponent(connectionId)}`,
    ),
  get: (id: string) => api.get<{ assistant: Agent }>(`${base}/${id}`).then((d) => d.assistant),
  create: (body: AgentDraft) =>
    api.post<{ assistant: Agent }>(base, body).then((d) => d.assistant),
  update: (id: string, body: Partial<AgentDraft> & { is_active?: boolean }) =>
    api.patch<{ assistant: Agent }>(`${base}/${id}`, body).then((d) => d.assistant),
  remove: (id: string) => api.delete<unknown>(`${base}/${id}`),

  threads: (id: string) =>
    api.get<{ threads: AgentThread[] }>(`${base}/${id}/threads`).then((d) => d.threads),
  createThread: (id: string, title?: string) =>
    api
      .post<{ thread: AgentThread }>(`${base}/${id}/threads`, { title })
      .then((d) => d.thread),
  thread: (id: string, threadId: string) =>
    api.get<{ thread: AgentThread; messages: AgentMessage[] }>(
      `${base}/${id}/threads/${threadId}`,
    ),
  renameThread: (id: string, threadId: string, title: string) =>
    api
      .patch<{ thread: AgentThread }>(`${base}/${id}/threads/${threadId}`, { title })
      .then((d) => d.thread),
  deleteThread: (id: string, threadId: string) =>
    api.delete<unknown>(`${base}/${id}/threads/${threadId}`),

  /**
   * One turn. Beyond the question, the same per-turn choices the Chat page
   * sends — a model for this question, a web mode, files — each optional and
   * absent meaning "as the agent is configured".
   */
  ask: (id: string, body: AskBody) => api.post<AgentAnswer>(`${base}/${id}/ask`, body),
}
